import React from 'react';
import { Helmet } from 'react-helmet-async';
import { SEO_CONFIG, getFullUrl } from './seoConfig';

interface SEOProps {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article';
  noindex?: boolean;
  structuredData?: object | object[];
  keywords?: string[];
}

/**
 * SEO Component
 * Handles dynamic meta tags, Open Graph, Twitter Cards, and structured data
 */
export const SEO: React.FC<SEOProps> = ({
  title,
  description = SEO_CONFIG.defaultDescription,
  image = SEO_CONFIG.defaultImage,
  url = '',
  type = 'website',
  noindex = false,
  structuredData,
  keywords,
}) => {
  // Construct full title with site name
  const fullTitle = title ? `${title} | ${SEO_CONFIG.siteName}` : SEO_CONFIG.defaultTitle;

  // Get full URL for canonical and Open Graph
  const fullUrl = url ? getFullUrl(url) : SEO_CONFIG.siteUrl;

  // Ensure image is a full URL
  const fullImage = image.startsWith('http') ? image : getFullUrl(image);

  // Use provided keywords or default from config
  const metaKeywords = keywords || SEO_CONFIG.keywords;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={metaKeywords.join(', ')} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Canonical URL */}
      <link rel="canonical" href={fullUrl} />

      {/* Open Graph */}
      <meta property="og:site_name" content={SEO_CONFIG.siteName} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:image" content={fullImage} />
      <meta property="og:locale" content={SEO_CONFIG.locale} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={SEO_CONFIG.twitterHandle} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImage} />

      {/* Structured Data (JSON-LD) */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(
            Array.isArray(structuredData) ? structuredData : structuredData
          )}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
